import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { UserPermissionService } from "clearline-common";
import { RoleTypes } from "core-kit";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AccountService } from "./account.service";
import { AuthService } from "./auth.service";

@Injectable()
export class AuthGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private accountSvc: AccountService,
    private userPermissionService: UserPermissionService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.userPermissionService.ready$.pipe(
      map(() => {
        let result = this.authService.isLoggedIn;
        if (!result) {
          this.authService.login();
          return result;
        }

        result = this.checkRole(route, state) && this.userPermissionService.hasRoutePermission(state.url);
        if (!result) {
          this.router.navigate(["/access-denied"]);

          return result;
        }

        result = this.isEmailVerified();
        if (!result) {
          this.router.navigate(["/verify"]);
          return result;
        }

        result = this.isManagerAccountConfigured(state);
        if (!result) {
          this.router.navigate(["/quick-setup"]);
          return result;
        }

        result = this.isPartnerAccountConfigured(state);
        if (!result) {
          this.router.navigate(["/partner-onboarding"]);
          return result;
        }

        return result;
      })
    );
  }

  private checkRole(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    let result = true;

    const roles = Object.keys(route.data).length ? (route.data.roles as string[]) : null;

    if (result && route.data && roles) {
      result = this.authService.isUserInRoles(roles);
      return result;
    }

    return true;
  }

  private isManagerAccountConfigured(state: RouterStateSnapshot) {
    const user = this.accountSvc.user;
    const result = user.doneQuickSetup || user.role !== RoleTypes.Manager;
    if (!result && !state.url.startsWith("/quick-setup")) {
      return false;
    }
    return true;
  }

  private isPartnerAccountConfigured(state: RouterStateSnapshot) {
    const user = this.accountSvc.user;
    // A partner that hasn't finished onboarding (doneQuickSetup) is forced into the wizard.
    const result = user.doneQuickSetup || user.role !== RoleTypes.PartnerAdmin;
    if (!result && !state.url.startsWith("/partner-onboarding")) {
      return false;
    }
    return true;
  }

  private isEmailVerified() {
    const user = this.accountSvc.user;
    const result = user && (user.email_verified || this.authService.requredMinimumRole(RoleTypes.PartnerAdmin));
    return result;
  }
}
